import { useCallback, useEffect, useMemo, useState } from 'react';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { bind, type UnbindFn } from 'bind-event-listener';

import { isFedrampModerate } from '@atlaskit/atlassian-context/is-fedramp-moderate';
import { fg } from '@atlaskit/platform-feature-flags';
import { functionWithFG } from '@atlaskit/platform-feature-flags-react';

import AnalyticsCrossProductInteractionClient from './analyticsCrossProductInteractionClient';
import { generateUrlWithParams } from './generateUrlWithParams';
import { INTERACTION_SESSION_ID_UPDATED_EVENT } from './interactionSessionTracking';

/**
 * @param bridge - The name of your navigation component e.g. atlassianSwitcher
 * @param product - The product you are hosted on e.g. jira
 * @param subProduct - If specified, will be appended to the end of product as: product-subProduct
 */
export type CrossProductUrlOptions = {
	bridge: string;
	product: string;
	subProduct?: string;
};

// Shared helper: decides whether the hook should be active for the current
// runtime. Identical logic to the pre-fix code — kept in one place so both
// the OLD and NEW implementations below stay byte-equivalent on this branch.
function computeIsEnabled(): boolean {
	const shouldDisable = fg('use_frontend_feature_encoding_sdk_platform')
		? !isFeatureEnabled(
				'atlassian-analytics',
				'atlassian-analytics-cross-product-interaction-client',
			)
		: isFedrampModerate();
	return !shouldDisable;
}

/**
 * Legacy implementation (gate `platform_fix_cross_product_url_rerender` OFF).
 *
 * Initialises the session id to an empty string and reads it from the client
 * inside a `useEffect` after mount. This causes one guaranteed re-render of
 * any consumer that depends on the returned wrapper.
 *
 * Kept verbatim so the rollback path is byte-equivalent to the pre-fix code.
 */
function useCrossProductUrlOLD(options: CrossProductUrlOptions): (url: string) => string {
	const [interactionSessionId, setInteractionSessionId] = useState<string>('');
	const { bridge, product, subProduct } = options;
	const isEnabled = computeIsEnabled();

	// Client does not change on re-render - cache with useMemo
	const interactionSessionClient = useMemo(() => {
		if (isEnabled) {
			return new AnalyticsCrossProductInteractionClient();
		} else {
			return null;
		}
	}, [isEnabled]);

	useEffect(() => {
		if (interactionSessionClient && isEnabled) {
			// Fetch any initial interaction session ID
			const currentSessionId = interactionSessionClient.getCurrentInteractionSessionId();
			currentSessionId && setInteractionSessionId(currentSessionId);

			// Add event listener that subscribes to any future interaction session ID updates
			const unbind: UnbindFn = bind(document, {
				type: INTERACTION_SESSION_ID_UPDATED_EVENT,
				listener: () => {
					const currentSessionId = interactionSessionClient.getCurrentInteractionSessionId();
					currentSessionId && setInteractionSessionId(currentSessionId);
				},
			});

			return unbind;
		}
		return () => {};
	}, [interactionSessionClient, isEnabled]);

	if (!isEnabled) {
		return (url: string) => url;
	}

	return (url: string) =>
		generateUrlWithParams(url, bridge, interactionSessionId, product, subProduct);
}

// Module-level identity wrapper used when the NEW hook is disabled. Stable
// identity avoids causing unnecessary re-renders / effect re-runs in
// downstream consumers.
const identityUrlWrapper = (url: string) => url;

/**
 * Fixed implementation (gate `platform_fix_cross_product_url_rerender` ON).
 *
 * - Lazy-initialises the session id from the client on the very first render,
 *   eliminating the post-mount re-render that re-generated the `xpis` query
 *   param.
 * - Returns a stable, memoised wrapper so consumer effects keyed on it do not
 *   re-run on unrelated re-renders.
 *
 * See PIRA-3829.
 */
function useCrossProductUrlNEW(options: CrossProductUrlOptions): (url: string) => string {
	const { bridge, product, subProduct } = options;
	const isEnabled = computeIsEnabled();

	const interactionSessionClient = useMemo(() => {
		if (isEnabled) {
			return new AnalyticsCrossProductInteractionClient();
		} else {
			return null;
		}
	}, [isEnabled]);

	const [interactionSessionId, setInteractionSessionId] = useState<string>(() => {
		if (isEnabled && interactionSessionClient) {
			return interactionSessionClient.getCurrentInteractionSessionId() ?? '';
		}
		return '';
	});

	useEffect(() => {
		if (interactionSessionClient && isEnabled) {
			// The lazy useState initializer above already read the session id on the
			// very first render, so we do NOT re-read here — that would cause the
			// avoidable post-mount re-render this fix is removing.

			const unbind: UnbindFn = bind(document, {
				type: INTERACTION_SESSION_ID_UPDATED_EVENT,
				listener: () => {
					const currentSessionId = interactionSessionClient.getCurrentInteractionSessionId();
					if (currentSessionId) {
						setInteractionSessionId((prev) =>
							prev === currentSessionId ? prev : currentSessionId,
						);
					}
				},
			});

			return unbind;
		}
		return () => {};
	}, [interactionSessionClient, isEnabled]);

	const wrapUrl = useCallback(
		(url: string) => generateUrlWithParams(url, bridge, interactionSessionId, product, subProduct),
		[bridge, interactionSessionId, product, subProduct],
	);

	if (!isEnabled) {
		return identityUrlWrapper;
	}

	return wrapUrl;
}

/**
 * This React hook is called with the correct bridge, product and sub-product parameters.
 * It returns a function that can be used to generate URLs with cross-product interaction parameters.
 *
 * The implementation is selected at call time by the
 * `platform_fix_cross_product_url_rerender` feature gate via `functionWithFG`.
 * This keeps the legacy and fixed code paths fully separated rather than
 * intermixed inside a single hook body. See PIRA-3829.
 *
 * @param options - Properties object that contains the following parameters:
 *
 * @returns A function that appends interaction session ID and other cross-product interaction parameters to a given URL.
 */
const useCrossProductUrl: typeof useCrossProductUrlOLD = functionWithFG(
	'platform_fix_cross_product_url_rerender',
	useCrossProductUrlNEW,
	useCrossProductUrlOLD,
);

export default useCrossProductUrl;
